import {
  TemplateCommentEditor,
  TemplatePlaceholderLegend
} from '@features/st-organizations/st-organization-message-templates-page'
import {
  selTemplateGroups,
  selOrganizationId,
  selOrganizationState,
  stOrganizationModule
} from '@features/st-organizations/st-organization-module'
import { useDerivedState } from '@st/react-util/use-derived-state'
import { useProcess, useSelect } from '~/store'
import { Button, Wizard } from '@st/theme'
import { ClientOrganizerMessageType, MessageTemplateSubjectVariant } from '@st/sdk'
import { ReactNode } from 'react'

export function STEditTemplateInlineWizardPage({
  title,
  subtitle,
  banner,
  onClose,
  onClickBack,
  onClickContinue,
  messageType
}: {
  title: string
  subtitle: string
  banner?: ReactNode
  messageType: ClientOrganizerMessageType
  onClose: () => void
  onClickBack: () => void
  onClickContinue: () => void
}) {
  const stOrganization = useProcess('stOrganization')
  const organizationId = useSelect('stOrganization', selOrganizationId)

  // See {@link selTemplateGroups} for more information on why we need template groups
  const templateGroup = useSelect('stOrganization', (s) => {
    const orgState = selOrganizationState(s)
    if (!orgState) return undefined
    return selTemplateGroups(orgState).find((tg) => tg.type === messageType)
  })

  if (!organizationId || !templateGroup) {
    return null
  }

  const tg = templateGroup

  const template = tg.templates[0]
  const defaultSubjectVariant =
    template.subjectVariantOptions[0]?.key ?? template.subjectVariant ?? 'questionnaire'
  const [comment, setComment] = useDerivedState(template.comment)
  const [subjectVariant, setSubjectVariant] = useDerivedState(defaultSubjectVariant)

  function handleClickContinue() {
    if (comment != template.comment || subjectVariant != template.subjectVariant) {
      stOrganization.send({
        type: 'saveMessageTemplates',
        messageTemplateIds: tg.templates.map((t) => t.id),
        comment,
        subjectVariant: subjectVariant as MessageTemplateSubjectVariant
      })
    }

    onClickContinue()
  }

  return (
    <Wizard.Scaffold
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button variant="default" onClick={onClickBack}>
            Back
          </Button>
          <Button variant="primary" onClick={handleClickContinue}>
            Continue
          </Button>
        </Wizard.BottomNav>
      }
    >
      <Wizard.Main variant="wide" title={title} subtitle={subtitle}>
        <div>
          {banner && <div className="m-auto w-[600px]">{banner}</div>}
          <div className="mt-4 flex items-start gap-5">
            {/* This is used to balance out the page so the textarea is centered */}
            <div className="w-[320px]" />

            <div className="flex w-[600px] flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label className="font-semibold">Subject</label>
                <select
                  className="h-12 rounded-lg border-0 py-1.5 pl-4 pr-12 text-gray-900 ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-indigo-600 disabled:opacity-60"
                  value={subjectVariant}
                  disabled={template.subjectVariantOptions.length <= 1}
                  onChange={(e) => setSubjectVariant(e.target.value as typeof subjectVariant)}
                >
                  {template.subjectVariantOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="font-semibold">Message</label>
                <TemplateCommentEditor template={template} value={comment} onChange={setComment} />
              </div>
            </div>

            <TemplatePlaceholderLegend template={template} />
          </div>
        </div>
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}
